// M3b.6 — types for seed/scenarios/dr_no_adventure.json.
//
// Verbatim shape verified by direct Read of dr_no_adventure.json (312 lines)
// in the M3b.6 prep session. The full canonical-data file is bundled at
// compile time via the `@seed` vite alias.
//
// Source: agentic_workflows/apps/briefing-room-module/seed/scenarios/dr_no_adventure.json
// Manifest: Core Rulebook Ch 20 The Island of Dr. No (p.148-156)

/**
 * Narrative (pure branching) decision edge — the default kind.
 * `kind` is absent or undefined to stay backward-compatible with
 * all existing scenario JSON files.
 */
export interface NarrativeEdge {
  kind?: undefined;
  label: string;
  /**
   * Next paragraph number to navigate to. `null` is observed in source at
   * paragraphs like ¶40 + ¶46 + ¶72 + ¶84, where the decision describes a
   * roll outcome (not a navigation edge); narrative_decisions[] carries
   * the actual next-paragraph routing in those cases.
   */
  leads_to_paragraph: number | null;
  note?: string;
}

/**
 * Skill-check decision edge (Workstream A, 2026-06-08).
 *
 * When the player clicks this edge the engine fires `executeRoll(skillName,
 * easeFactor)`, plays the dice cinematic, and then routes to the paragraph
 * specified by the rolled QR tier (qr1..qr4) or failure.
 *
 * `leads_to_paragraph` is intentionally absent — routing is determined by
 * the roll outcome via `outcomes`, not by a static target paragraph.
 */
export interface SkillCheckEdge {
  kind: 'skill_check';
  label: string;
  /** Skill name — must resolve via resolveSkillToPregenKey (BOND_RPG_SKILLS / SKILL_ALIASES). */
  skillName: string;
  /** Ease Factor 1–10 per Core Rulebook p.14–15. */
  easeFactor: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
  /** Per-QR-tier routing. All five keys required so authors can't silently omit an outcome. */
  outcomes: {
    qr1: number; // Critical Success
    qr2: number;
    qr3: number;
    qr4: number; // Acceptable
    failure: number;
  };
  note?: string;
}

/**
 * One choice in a paragraph's `decisions[]` or `narrative_decisions[]`.
 *
 * Discriminated union: `kind === 'skill_check'` → SkillCheckEdge;
 * `kind === undefined` (or absent in JSON) → NarrativeEdge.
 */
export type ScenarioDecisionEdge = NarrativeEdge | SkillCheckEdge;

/** Canonical ending types observed in source at ¶29, ¶44, ¶83, ¶94, ¶97, ¶102. */
export type EndingType =
  | 'victory'
  | 'rescue_victory_sub_route'
  | 'pyrrhic_victory_death'
  | 'death'
  | 'catastrophic_failure';

/** One numbered story paragraph (1-102) from `story_paragraphs[]`. */
export interface ScenarioParagraph {
  number: number;
  page: number;
  text: string;
  /** Variant 1 — branching choice; absent for pure narrative or immediate-transition paragraphs. */
  decisions?: ScenarioDecisionEdge[];
  /** Variant 2 — auto-advance with no UI choice (e.g., ¶4 → ¶92, ¶34 → ¶29). */
  leads_to_paragraph_immediate?: number;
  /** Variant 3 — two-phase post-roll (¶46, ¶51, ¶72, ¶84). Describes outcome AFTER the roll. */
  post_roll_narrative?: string;
  /** Variant 3 continued — the actual next-paragraph choices after the roll. */
  narrative_decisions?: ScenarioDecisionEdge[];
  /** Side-channel GM notes (annotation, not narrative). */
  gm_notes?: string[];
  /** Variant 4 — terminal ending. */
  is_ending?: boolean;
  /** ¶102 carries both is_partial_ending + a leads_to_paragraph_immediate to chain into ¶29. */
  is_partial_ending?: boolean;
  ending_type?: EndingType;
  /**
   * V7.1 NPC character sheets (2026-06-02). When set, the SPA renders an
   * inline NpcCard for each id after the paragraph text. ids are matched
   * against scenario.key_npcs[].id + 'guards_archetype' (the special
   * top-level mook record).
   */
  npc_inline_refs?: string[];
  // Off-range note keys (note_paragraph_103_offrange, note_paragraph_108_offrange,
  // note_paragraph_107_offrange, note_offrange, etc.) are documented in the
  // raw JSON but are not pre-typed here — the runner enumerates own-keys
  // matching /^note_(paragraph_\d+_)?offrange$/ at runtime.
}

/** The Q-branch equipment table from character_briefing. */
export interface QBranchEquipmentItem {
  item: string;
  given_to: string;
  note: string;
}

/**
 * The initial state source — NOT a story_paragraphs[] entry. The runner
 * mounts on a synthetic "briefing" pseudo-paragraph rendering
 * briefing_text_verbatim + two decisions: A → ¶99 (wait) or B → ¶33 (search).
 */
export interface CharacterBriefing {
  page: number;
  title: string;
  briefing_text_verbatim: string;
  missing_operative_target: string;
  cover_identity: string;
  cover_assignment: string;
  facility_to_investigate: string;
  target_npc_for_meeting: string;
  departure_point: string;
  transport_to_island: string;
  q_branch_equipment_issued: QBranchEquipmentItem[];
  key_facts_about_holck: Record<string, string>;
  /**
   * 2026-06-07 (V9→V7 prep): generalized from a 2-key dr_no-specific object
   * to ScenarioDecisionEdge[] so other modules (A View to a Kill, Diamonds
   * Never Die, Code Red) can author their own briefing-button labels. The
   * array typically has two entries (A/B) but is not constrained. Labels
   * should start with "A. ", "B. ", etc. for voice-trigger matching.
   */
  first_decision_paragraphs: ScenarioDecisionEdge[];
  /**
   * V7.1 NPC character sheets (2026-06-02). When set, the SPA renders
   * inline NpcCards beneath the briefing text. Closes the user-reported
   * "her characteristics are as follows:" papercut by surfacing Pamela
   * Holck's stat block right where the briefing text promises it.
   */
  briefing_npc_inline_refs?: string[];
}

export interface ScenarioEndingDescriptor {
  paragraph: number;
  type: string;
  narrative_summary: string;
  trigger_paths?: string;
}

export interface ScenarioEndingsIndex {
  description: string;
  victory_endings: ScenarioEndingDescriptor[];
  death_endings: ScenarioEndingDescriptor[];
  catastrophic_failure_endings: ScenarioEndingDescriptor[];
  total_endings: number;
}

/**
 * V7.1 NPC character sheets (2026-06-02) — raw shape of a key_npcs entry
 * verified against scenario.key_npcs[0] (Pamela Holck) on 2026-06-02.
 */
export interface KeyNpcRaw {
  id: string;
  name: string;
  role: string;
  manifest_section?: string;
  page?: number;
  stats?: Record<string, number>;
  skills?: Record<string, { skill_level: number; primary_chance: number }>;
  physical?: Record<string, string | number>;
  combat?: Record<string, string | number>;
  allegiance?: string;
  encounter_paragraph_first_appearance?: number;
  biographical_note?: string;
}

/**
 * Top-level mook record. Shape verified against scenario.guards_archetype
 * on 2026-06-02. Lacks an `id` (the SPA assigns 'guards_archetype' as the
 * synthetic id for the inline-refs system); uses `description_verbatim`
 * as the sketch field.
 */
export interface GuardsArchetypeRaw {
  page: number;
  title: string;
  description_verbatim: string;
  stats?: Record<string, number>;
  skills?: Record<string, { skill_level: number; primary_chance: number }>;
  physical?: Record<string, string | number>;
  weapon?: string;
  engine_cross_ref?: Record<string, unknown>;
}

/**
 * V7.1 NPC character sheets (2026-06-02) — UI-normalized NPC data the
 * NpcCard + NpcDrawer render. Produced by useNpcLookup from KeyNpcRaw or
 * GuardsArchetypeRaw.
 */
export interface NpcSheetData {
  id: string;
  name: string;
  role: string;
  sketch: string;
  stats: Record<string, number>;
  skills: Record<string, { skill_level: number; primary_chance: number }>;
  physical: Record<string, string | number>;
  combat: Record<string, string | number>;
  isMook: boolean;
  /** Core Rulebook page reference when present, surfaced inline for verification. */
  page?: number;
  allegiance?: string;
}

/** Top-level shape of dr_no_adventure.json. */
export interface AdventureScenario {
  schema_version: string;
  source: string;
  source_manifest: string;
  description: string;
  total_story_paragraphs: number;
  pre_adventure_review_note: {
    page: number;
    verbatim: string;
    you_pronoun_meaning: string;
  };
  character_briefing: CharacterBriefing;
  story_paragraphs: ScenarioParagraph[];
  /** V7.1: scenario-specific named NPCs. */
  key_npcs?: KeyNpcRaw[];
  /** V7.1: top-level mook archetype shared by guard encounters. */
  guards_archetype?: GuardsArchetypeRaw;
  endings_index: ScenarioEndingsIndex;
  off_range_paragraph_references_note: string;
}

// ─── Runner state (NOT in JSON; lives in component / localStorage) ──────

export interface RollLog {
  paragraph: number;
  skill_name?: string;
  ease_factor: string;
  primary_chance: number;
  success_chance: number;
  d100_roll: number;
  quality_rating: number | null;
  quality_name: string;
}

export type RunnerView =
  | { kind: 'briefing' }
  | { kind: 'paragraph'; number: number }
  | { kind: 'ending'; paragraph: number; ending_type: EndingType };

export type DrawerSection =
  | 'stats'
  | 'history'
  | 'roll'
  | 'audio'
  | 'voice-log'
  | 'npcs'
  | 'pdf-library'
  | 'modules-library'
  | 'equipment-library'
  | 'vehicles-library'
  | 'gadgets-library'
  | 'drugs-library'
  | 'q-branch-personnel-library'
  | 'npc-archetypes-library'
  | 'canonical-npcs-library'
  | 'villains-dossier-library'
  | 'thrilling-locations-npcs-library'
  | 'atmosphere'
  | 'theme'
  | null;

// Round 14 (2026-06-06, Sprint 2 §4.4): generic Save / Damage / Skill
// Check / Percentile rolls fired from the Other Rolls panel (or the
// voice force_dice_roll tool) get appended here so they appear in the
// History drawer's "Other Rolls" section. Distinct from RollLog because
// the payload semantics differ (notation + dice-box breakdown vs PC × EF
// skill check). Kept as a separate field on ScenarioRunnerState to avoid
// retrofitting RollLog with a discriminant.
export interface GenericRollEntry {
  mode: 'save' | 'damage' | 'skill_check' | 'percentile';
  /** Dice notation as parsed by dice-box, e.g. "1d20", "2d6", "3d8+2". */
  notation: string;
  /** Chip total: percentile concatenation OR plain sum of breakdown. */
  total: number;
  /** Per-die face values returned by dice-box (in roll order). */
  breakdown: number[];
  /** Date.now() at the moment the dice settled. */
  timestamp: number;
}

/**
 * Current canonical version of the persisted ScenarioRunnerState shape.
 * Bump when a breaking change is introduced; readScenarioState will reject
 * stored blobs whose schemaVersion does not match (or whose schemaVersion
 * is absent, which is treated as the legacy pre-versioning state — also
 * accepted at v1, since the legacy shape is structurally compatible with
 * v1's read-tolerant optional fields).
 */
export const SCENARIO_STATE_SCHEMA_VERSION = 1 as const;

export interface ScenarioRunnerState {
  /**
   * Schema-version sentinel. Stamped on every writeScenarioState; checked
   * on every readScenarioState (mismatch → reject, falls through to
   * DEFAULT_STATE). Optional on the type because pre-2026-06-08 stored
   * blobs lacked the field and existing in-memory state construction
   * doesn't need to know about it; the WRITER guarantees the field
   * appears on every write, and the READER enforces correct-or-absent.
   */
  schemaVersion?: typeof SCENARIO_STATE_SCHEMA_VERSION;
  view: RunnerView;
  history: number[];
  rolls: RollLog[];
  // 2026-06-05 persistence triple: which side drawer is open. Optional so
  // pre-1.270.0 stored state still round-trips through readScenarioState
  // without rejection — absent → null → no drawer open.
  drawer?: DrawerSection;
  // Round 14 (2026-06-06, Sprint 2 §4.4): generic Save/Damage/Skill
  // Check/Percentile rolls. Optional + array-typed so pre-1.296.0 stored
  // state round-trips through readScenarioState — absent → undefined →
  // initialize to [] at consumer.
  genericRolls?: GenericRollEntry[];
}
